import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Check, Mail, Flag, ExternalLink, Link2, MapPin, Shield, FileText, Calendar, ShoppingBag, Repeat2, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { accountsApi, badgesApi, adminApi } from '@/api'
import { formatDateTime } from '@/lib/dates'
import { useAuthStore } from '@/store/auth.store'
import { Avatar } from '@/components/profile/Avatar'
import { BadgeList } from '@/components/profile/BadgeList'
import { FeedList } from '@/components/feed'
import { useToast } from '@/components/common/Toast'
import type { FeedItem, Badge } from '@olimhub/shared'
import { SYSTEM_BADGE_SLUGS } from '@olimhub/shared'

// ─── Skeleton ────────────────────────────────────────────────────────────────

function ProfileSkeleton() {
  return (
    <div style={{ background: 'var(--surface)' }}>
      {/* Cover */}
      <div style={{ height: 116, background: 'var(--surface-2)', opacity: 0.6 }} />
      {/* Avatar + name */}
      <div style={{ padding: '0 16px 14px' }}>
        <div
          style={{
            marginTop: -38,
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'var(--border)',
            opacity: 0.6,
          }}
        />
        <div style={{ marginTop: 10, width: 160, height: 22, background: 'var(--border)', borderRadius: 'var(--radius-sm)', opacity: 0.6 }} />
        <div style={{ marginTop: 6, width: 100, height: 16, background: 'var(--border)', borderRadius: 'var(--radius-sm)', opacity: 0.6 }} />
      </div>
      {/* Stats */}
      <div style={{ display: 'flex', padding: '4px 8px 14px', gap: 8 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ flex: 1, height: 44, background: 'var(--border)', borderRadius: 'var(--radius-sm)', opacity: 0.6 }} />
        ))}
      </div>
      {/* About */}
      <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ width: '80%', height: 15, background: 'var(--border)', borderRadius: 'var(--radius-sm)', opacity: 0.6 }} />
        <div style={{ width: '60%', height: 15, background: 'var(--border)', borderRadius: 'var(--radius-sm)', opacity: 0.6 }} />
      </div>
    </div>
  )
}

// ─── ProfilePage ──────────────────────────────────────────────────────────────

type ProfileTab = 'posts' | 'boosts' | 'events' | 'offers' | 'journey'

export function ProfilePage() {
  const { handle } = useParams<{ handle: string }>()
  const username = handle?.replace(/^@/, '') ?? ''
  const navigate = useNavigate()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { account: authAccount, isAuthenticated, hasCapability } = useAuthStore()
  const { toast } = useToast()

  const [activeTab, setActiveTab] = useState<ProfileTab>('posts')
  const [localIsFollowing, setLocalIsFollowing] = useState<boolean | undefined>(undefined)
  const [reported, setReported] = useState(false)
  const [showBadgeManager, setShowBadgeManager] = useState(false)
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null)
  const [confirmGrant, setConfirmGrant] = useState<Badge | null>(null)
  const [badgeWorking, setBadgeWorking] = useState(false)
  const [suggestedWorking, setSuggestedWorking] = useState(false)

  const isOwnProfile = authAccount?.username === username
  const canManageBadges = hasCapability('grant_badges') && !isOwnProfile

  const { data: allBadges } = useQuery({
    queryKey: ['badges'],
    queryFn: () => badgesApi.getAll(),
    enabled: showBadgeManager && canManageBadges,
  })

  const { data: account, isLoading, isError } = useQuery({
    queryKey: ['profile', username],
    queryFn: () => accountsApi.getByUsername(username!),
    enabled: !!username,
  })

  const effectiveIsFollowing = localIsFollowing ?? account?.isFollowing ?? false

  // Posts infinite query
  const {
    data: postsData,
    hasNextPage: postsHasMore,
    fetchNextPage: postsFetchNext,
    isFetching: postsIsFetching,
    isFetchingNextPage: postsIsFetchingMore,
  } = useInfiniteQuery({
    queryKey: ['profile-posts', username, 'post'],
    queryFn: ({ pageParam }) =>
      accountsApi.getUserPosts(username!, { cursor: pageParam as string | undefined, type: 'post' }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!username && activeTab === 'posts',
  })

  // Boosts infinite query
  const {
    data: boostsData,
    hasNextPage: boostsHasMore,
    fetchNextPage: boostsFetchNext,
    isFetching: boostsIsFetching,
    isFetchingNextPage: boostsIsFetchingMore,
  } = useInfiniteQuery({
    queryKey: ['profile-boosts', username],
    queryFn: ({ pageParam }) =>
      accountsApi.getUserBoosts(username!, { cursor: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!username && activeTab === 'boosts',
  })

  // Events infinite query
  const {
    data: eventsData,
    hasNextPage: eventsHasMore,
    fetchNextPage: eventsFetchNext,
    isFetching: eventsIsFetching,
    isFetchingNextPage: eventsIsFetchingMore,
  } = useInfiniteQuery({
    queryKey: ['profile-posts', username, 'event'],
    queryFn: ({ pageParam }) =>
      accountsApi.getUserPosts(username!, { cursor: pageParam as string | undefined, type: 'event' }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!username && activeTab === 'events',
  })

  // Offers infinite query
  const {
    data: offersData,
    hasNextPage: offersHasMore,
    fetchNextPage: offersFetchNext,
    isFetching: offersIsFetching,
    isFetchingNextPage: offersIsFetchingMore,
  } = useInfiniteQuery({
    queryKey: ['profile-posts', username, 'offer'],
    queryFn: ({ pageParam }) =>
      accountsApi.getUserPosts(username!, { cursor: pageParam as string | undefined, type: 'offer' }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!username && activeTab === 'offers',
  })

  // Jornada tab — attested Action participations. No pagination (MVP: no
  // account is expected to accumulate enough to need it yet — see
  // actions.service.ts listParticipationsForAccount).
  const { data: participations, isFetching: participationsIsFetching } = useQuery({
    queryKey: ['profile-participations', username],
    queryFn: () => accountsApi.getParticipations(username!),
    enabled: !!username && activeTab === 'journey',
  })

  async function handleFollow() {
    if (!isAuthenticated) return
    const prev = effectiveIsFollowing
    setLocalIsFollowing(!prev)
    try {
      if (prev) {
        await accountsApi.unfollow(username!)
      } else {
        await accountsApi.follow(username!)
      }
      void queryClient.invalidateQueries({ queryKey: ['profile', username] })
      void queryClient.invalidateQueries({ queryKey: ['profile', authAccount?.username] })
      void queryClient.invalidateQueries({ queryKey: ['followers', username] })
      void queryClient.invalidateQueries({ queryKey: ['following', authAccount?.username] })
      void queryClient.invalidateQueries({ queryKey: ['feed'] })
    } catch {
      setLocalIsFollowing(prev)
    }
  }

  async function handleReport() {
    if (!isAuthenticated || !username) return
    try {
      await accountsApi.report(username, 'reported_by_user')
      setReported(true)
    } catch {
      // silent fail
    }
  }

  async function handleGrantBadge() {
    if (!confirmGrant || !username) return
    setBadgeWorking(true)
    try {
      await badgesApi.grant(username, confirmGrant.slug)
      void queryClient.invalidateQueries({ queryKey: ['profile', username] })
      toast.success(`Badge "${confirmGrant.name}" granted`)
    } catch {
      toast.error('Could not grant badge')
    } finally {
      setBadgeWorking(false)
      setConfirmGrant(null)
    }
  }

  async function handleRevokeBadge() {
    if (!confirmRevoke) return
    setBadgeWorking(true)
    try {
      await badgesApi.revoke(confirmRevoke)
      void queryClient.invalidateQueries({ queryKey: ['profile', username] })
      toast.success('Badge revoked')
    } catch {
      toast.error('Could not revoke badge')
    } finally {
      setBadgeWorking(false)
      setConfirmRevoke(null)
    }
  }

  async function handleToggleSuggested(newValue: boolean) {
    if (!account) return
    setSuggestedWorking(true)
    try {
      await adminApi.toggleSuggested(account.id, newValue)
      void queryClient.invalidateQueries({ queryKey: ['profile', username] })
      toast.success(newValue ? 'Pinned as suggested account' : 'Removed from suggestions')
    } catch {
      toast.error('Could not update suggestion status')
    } finally {
      setSuggestedWorking(false)
    }
  }

  if (isLoading) {
    return <ProfileSkeleton />
  }

  if (isError || !account) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '64px 24px',
          fontFamily: 'var(--font-sans)',
          color: 'var(--text-muted)',
        }}
      >
        <p style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: 'var(--text-strong)', marginBottom: 8 }}>
          User not found
        </p>
        <Link to="/" style={{ color: 'var(--text-link)', fontSize: 'var(--fs-sm)' }}>
          Back to home
        </Link>
      </div>
    )
  }

  const postsItems: FeedItem[] = postsData?.pages.flatMap((p) => p.items) ?? []
  const boostsItems: FeedItem[] = boostsData?.pages.flatMap((p) => p.items) ?? []
  const eventsItems: FeedItem[] = eventsData?.pages.flatMap((p) => p.items) ?? []
  const offersItems: FeedItem[] = offersData?.pages.flatMap((p) => p.items) ?? []

  const tabs: Array<{ key: ProfileTab; label: string }> = [
    { key: 'posts', label: 'Posts' },
    { key: 'boosts', label: 'Boosts' },
    { key: 'events', label: 'Events' },
    { key: 'offers', label: 'Bazaar' },
    { key: 'journey', label: t('actions.journey_tab') },
  ]

  return (
    <div style={{ maxWidth: 'var(--feed-max)', margin: '0 auto', background: 'var(--surface)' }}>
      {/* Cover + Header */}
      <div style={{ position: 'relative', background: 'var(--surface)' }}>
        {/* Cover band */}
        <div style={{ height: 116, position: 'relative' }}>
          <div
            className="oh-spectrum oh-spectrum--smooth"
            style={{ position: 'absolute', inset: 0, height: '100%', borderRadius: 0 }}
          />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(180deg, rgba(15,17,23,0.28), transparent 46%)',
            }}
          />
          {/* Top controls */}
          <div
            style={{
              position: 'absolute',
              top: 12,
              insetInlineStart: 12,
              insetInlineEnd: 12,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <button
              type="button"
              aria-label="Go back"
              onClick={() => navigate(-1)}
              style={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                border: 'none',
                background: 'rgba(15,17,23,0.38)',
                backdropFilter: 'blur(8px)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <ArrowLeft size={20} />
            </button>
          </div>
        </div>

        {/* Avatar + name area */}
        <div style={{ padding: '0 16px 14px', position: 'relative', zIndex: 1 }}>
          <div style={{ marginTop: -38 }}>
            <Avatar
              name={account.displayName || account.username}
              avatarUrl={account.avatarUrl}
              animalAvatar={account.animalAvatar}
              accountType={account.accountType}
              size="lg"
              style={{ boxShadow: '0 0 0 4px var(--surface), var(--shadow-card)' }}
            />
          </div>
          <h1
            style={{
              marginTop: 10,
              marginBottom: 0,
              fontSize: 'var(--fs-h2)',
              fontWeight: 700,
              color: 'var(--text-strong)',
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
              fontFamily: 'var(--font-sans)',
            }}
          >
            {account.displayName || account.username}
          </h1>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 2,
              color: 'var(--text-muted)',
              fontSize: 'var(--fs-sm)',
              fontFamily: 'var(--font-sans)',
            }}
          >
            <span>@{account.username}</span>
          </div>
          {account.badges.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <BadgeList badges={account.badges} />
            </div>
          )}
        </div>
      </div>

      {/* Stats rows */}
      <div style={{ padding: '4px 8px 14px', background: 'var(--surface)', display: 'flex', flexDirection: 'column', gap: 0 }}>
        {/* Row 1: content counts */}
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          {[
            { value: account.stats.postsCount, label: 'Posts', onClick: () => setActiveTab('posts') },
            { value: account.stats.eventsCount, label: 'Events', onClick: () => setActiveTab('events') },
            { value: account.stats.offersCount, label: 'Bazaar', onClick: () => setActiveTab('offers') },
          ].map((stat, i, arr) => (
            <div key={stat.label} style={{ display: 'flex', flex: 1, alignItems: 'stretch' }}>
              <button
                type="button"
                onClick={stat.onClick}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: '4px 0',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 'var(--fs-body-lg)', color: 'var(--text-strong)' }}>
                  {stat.value}
                </span>
                <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                  {stat.label}
                </span>
              </button>
              {i < arr.length - 1 && (
                <div style={{ width: 1, background: 'var(--border)', margin: '4px 0' }} />
              )}
            </div>
          ))}
        </div>

        <div style={{ height: 1, background: 'var(--border)', margin: '2px 0' }} />

        {/* Row 2: social counts */}
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          <button
            type="button"
            onClick={() => navigate(`/@${account.username}/followers`)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '4px 0',
              fontFamily: 'var(--font-sans)',
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 'var(--fs-body-lg)', color: 'var(--text-strong)' }}>
              {account.stats.followersCount}
            </span>
            <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
              Followers
            </span>
          </button>

          <div style={{ width: 1, background: 'var(--border)', margin: '4px 0' }} />

          <button
            type="button"
            onClick={() => navigate(`/@${account.username}/following`)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '4px 0',
              fontFamily: 'var(--font-sans)',
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 'var(--fs-body-lg)', color: 'var(--text-strong)' }}>
              {account.stats.followingCount}
            </span>
            <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
              Following
            </span>
          </button>
        </div>
      </div>

      {/* About + Location + Links */}
      {(account.about || account.city || account.links.length > 0) && (
        <div
          style={{
            padding: '0 16px 16px',
            background: 'var(--surface)',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {account.about && (
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-body)',
                color: 'var(--text-body)',
                lineHeight: 'var(--lh-normal)',
              }}
            >
              {account.about}
            </p>
          )}
          {account.city && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <MapPin size={14} color="var(--text-muted)" strokeWidth={2} />
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text-muted)',
                }}
              >
                {account.city}
              </span>
            </div>
          )}
          {account.links.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {account.links.map((link) => (
                <div key={link.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {link.url ? (
                    <>
                      <ExternalLink size={14} color="var(--text-link)" strokeWidth={2} />
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontFamily: 'var(--font-sans)',
                          fontSize: 'var(--fs-sm)',
                          color: 'var(--text-link)',
                          fontWeight: 600,
                          textDecoration: 'none',
                        }}
                      >
                        {link.label}
                      </a>
                    </>
                  ) : (
                    <>
                      <Link2 size={14} color="var(--text-muted)" strokeWidth={2} />
                      <span
                        style={{
                          fontFamily: 'var(--font-sans)',
                          fontSize: 'var(--fs-sm)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {link.label}
                      </span>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action row */}
      {!isOwnProfile && (
        <div
          style={{
            padding: '0 16px 16px',
            background: 'var(--surface)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={() => {
              if (!isAuthenticated) { navigate(`/login?from=/@${account.username}`); return }
              void handleFollow()
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '9px 20px',
              borderRadius: 'var(--radius-pill)',
              border: effectiveIsFollowing ? '1.5px solid var(--border-strong)' : 'none',
              background: effectiveIsFollowing ? 'transparent' : 'var(--brand)',
              color: effectiveIsFollowing ? 'var(--text-strong)' : 'var(--on-brand)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 'var(--fs-sm)',
              cursor: 'pointer',
              minHeight: 'var(--tap-min)',
            }}
          >
            {effectiveIsFollowing && <Check size={15} />}
            {effectiveIsFollowing ? 'Following' : 'Follow'}
          </button>

          <button
            type="button"
            onClick={() => {
              if (!isAuthenticated) { navigate(`/login?from=/@${account.username}`); return }
              navigate(`/messages/${account.username}`)
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '9px 20px',
              borderRadius: 'var(--radius-pill)',
              border: '1.5px solid var(--border-strong)',
              background: 'transparent',
              color: 'var(--text-strong)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 'var(--fs-sm)',
              cursor: 'pointer',
              minHeight: 'var(--tap-min)',
            }}
          >
            <Mail size={15} />
            Message
          </button>

          <button
            type="button"
            onClick={() => {
              if (!isAuthenticated) { navigate(`/login?from=/@${account.username}`); return }
              void handleReport()
            }}
            aria-label="Report user"
            style={{
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-pill)',
              border: '1.5px solid var(--border-strong)',
              background: 'transparent',
              color: reported ? 'var(--danger)' : 'var(--text-muted)',
              cursor: 'pointer',
            }}
          >
            <Flag size={16} />
          </button>
        </div>
      )}

      {isOwnProfile && (
        <div style={{ padding: '0 16px 16px', background: 'var(--surface)' }}>
          <Link
            to="/settings"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '9px 20px',
              borderRadius: 'var(--radius-pill)',
              border: '1.5px solid var(--border-strong)',
              background: 'transparent',
              color: 'var(--text-strong)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 'var(--fs-sm)',
              textDecoration: 'none',
              minHeight: 'var(--tap-min)',
            }}
          >
            Edit profile
          </Link>
        </div>
      )}

      {/* Badge management — visible to users with grant_badges capability */}
      {canManageBadges && (
        <div style={{ padding: '0 16px 16px', background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
          {/* Suggested account toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-body)' }}>
              {t('suggestions.pin_as_suggested')}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={account?.isSuggested ?? false}
              disabled={suggestedWorking}
              onClick={() => void handleToggleSuggested(!(account?.isSuggested ?? false))}
              style={{
                width: 44,
                height: 26,
                borderRadius: 13,
                border: 'none',
                background: account?.isSuggested ? 'var(--brand)' : 'var(--border-strong)',
                position: 'relative',
                cursor: suggestedWorking ? 'not-allowed' : 'pointer',
                opacity: suggestedWorking ? 0.6 : 1,
                flexShrink: 0,
                transition: 'background var(--dur-fast) var(--ease-out)',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 3,
                  insetInlineStart: account?.isSuggested ? 21 : 3,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: '#fff',
                  transition: 'inset-inline-start var(--dur-fast) var(--ease-out)',
                }}
              />
            </button>
          </div>

          <button
            type="button"
            onClick={() => setShowBadgeManager((v) => !v)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 'var(--radius-pill)',
              border: '1.5px solid var(--border-strong)', background: 'transparent',
              color: 'var(--text-muted)', cursor: 'pointer',
              fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-sm)',
              marginTop: 12,
            }}
          >
            <Shield size={14} />
            Manage badges
          </button>

          {showBadgeManager && (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Granted badges with revoke option */}
              {account?.badges
                .filter((ab) => ab.badge.slug !== SYSTEM_BADGE_SLUGS.SUPER_ADMIN)
                .map((ab) => (
                  <div key={ab.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ flex: 1, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-body)' }}>
                      {ab.badge.name}
                    </span>
                    {confirmRevoke === ab.id ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          disabled={badgeWorking}
                          onClick={handleRevokeBadge}
                          style={{
                            padding: '4px 10px', borderRadius: 'var(--radius-pill)',
                            border: 'none', background: 'var(--danger)', color: '#fff',
                            fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-xs)',
                            cursor: badgeWorking ? 'not-allowed' : 'pointer', opacity: badgeWorking ? 0.6 : 1,
                          }}
                        >
                          Confirm revoke
                        </button>
                        <button type="button" onClick={() => setConfirmRevoke(null)} style={{ padding: '4px 10px', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmRevoke(ab.id)}
                        style={{
                          padding: '4px 10px', borderRadius: 'var(--radius-pill)',
                          border: '1px solid var(--border-strong)', background: 'transparent',
                          color: 'var(--danger)', fontFamily: 'var(--font-sans)', fontWeight: 600,
                          fontSize: 'var(--fs-xs)', cursor: 'pointer',
                        }}
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                ))}

              {/* Available badges to grant */}
              {allBadges
                ?.filter((b) =>
                  b.slug !== SYSTEM_BADGE_SLUGS.SUPER_ADMIN &&
                  !account?.badges.some((ab) => ab.badge.slug === b.slug)
                )
                .map((badge) => (
                  <div key={badge.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ flex: 1, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
                      {badge.name}
                    </span>
                    {confirmGrant?.id === badge.id ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          type="button"
                          disabled={badgeWorking}
                          onClick={handleGrantBadge}
                          style={{
                            padding: '4px 10px', borderRadius: 'var(--radius-pill)',
                            border: 'none', background: 'var(--brand)', color: 'var(--on-brand)',
                            fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-xs)',
                            cursor: badgeWorking ? 'not-allowed' : 'pointer', opacity: badgeWorking ? 0.6 : 1,
                          }}
                        >
                          Confirm grant
                        </button>
                        <button type="button" onClick={() => setConfirmGrant(null)} style={{ padding: '4px 10px', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmGrant(badge)}
                        style={{
                          padding: '4px 10px', borderRadius: 'var(--radius-pill)',
                          border: '1px solid var(--border-strong)', background: 'transparent',
                          color: 'var(--brand)', fontFamily: 'var(--font-sans)', fontWeight: 600,
                          fontSize: 'var(--fs-xs)', cursor: 'pointer',
                        }}
                      >
                        Grant
                      </button>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Profile tabs */}
      <div
        style={{
          display: 'flex',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'var(--surface)',
          borderTop: '1px solid var(--border)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        {tabs.map((tab) => {
          const active = activeTab === tab.key
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1,
                height: 'var(--header-tabs)',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                position: 'relative',
                fontFamily: 'var(--font-sans)',
                fontWeight: active ? 700 : 500,
                fontSize: 'var(--fs-sm)',
                color: active ? 'var(--text-strong)' : 'var(--text-muted)',
                transition: 'color var(--dur-fast) var(--ease-out)',
              }}
            >
              {tab.label}
              {active && (
                <span
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    insetInlineStart: '50%',
                    transform: 'translateX(-50%)',
                    width: 28,
                    height: 3,
                    borderRadius: 3,
                    background: 'var(--brand)',
                  }}
                />
              )}
            </button>
          )
        })}
      </div>

      {/* Profile feed */}
      <div style={{ background: 'var(--bg-sunk)', minHeight: 200 }}>
        {activeTab === 'posts' && (
          <FeedList
            items={postsItems}
            hasMore={!!postsHasMore}
            isLoading={postsIsFetching && postsItems.length === 0}
            isFetchingMore={postsIsFetchingMore}
            onLoadMore={() => void postsFetchNext()}
            readOnly={false}
            emptyNode={
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 24px', textAlign: 'center' }}>
                <FileText size={48} strokeWidth={1.5} style={{ color: 'var(--text-faint)', marginBottom: 12 }} />
                <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
                  No posts yet
                </p>
              </div>
            }
          />
        )}

        {activeTab === 'events' && (
          <FeedList
            items={eventsItems}
            hasMore={!!eventsHasMore}
            isLoading={eventsIsFetching && eventsItems.length === 0}
            isFetchingMore={eventsIsFetchingMore}
            onLoadMore={() => void eventsFetchNext()}
            readOnly={false}
            emptyNode={
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 24px', textAlign: 'center' }}>
                <Calendar size={48} strokeWidth={1.5} style={{ color: 'var(--text-faint)', marginBottom: 12 }} />
                <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
                  No events yet
                </p>
              </div>
            }
          />
        )}

        {activeTab === 'boosts' && (
          <FeedList
            items={boostsItems}
            hasMore={!!boostsHasMore}
            isLoading={boostsIsFetching && boostsItems.length === 0}
            isFetchingMore={boostsIsFetchingMore}
            onLoadMore={() => void boostsFetchNext()}
            readOnly={false}
            emptyNode={
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 24px', textAlign: 'center' }}>
                <Repeat2 size={48} strokeWidth={1.5} style={{ color: 'var(--text-faint)', marginBottom: 12 }} />
                <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
                  No boosts yet
                </p>
              </div>
            }
          />
        )}

        {activeTab === 'offers' && (
          <FeedList
            items={offersItems}
            hasMore={!!offersHasMore}
            isLoading={offersIsFetching && offersItems.length === 0}
            isFetchingMore={offersIsFetchingMore}
            onLoadMore={() => void offersFetchNext()}
            readOnly={false}
            emptyNode={
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 24px', textAlign: 'center' }}>
                <ShoppingBag size={48} strokeWidth={1.5} style={{ color: 'var(--text-faint)', marginBottom: 12 }} />
                <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
                  No listings yet
                </p>
              </div>
            }
          />
        )}

        {activeTab === 'journey' && (
          participationsIsFetching && !participations ? (
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[0, 1].map((i) => (
                <div key={i} style={{ height: 56, background: 'var(--border)', borderRadius: 'var(--radius-md)', opacity: 0.4 }} />
              ))}
            </div>
          ) : !participations || participations.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 24px', textAlign: 'center' }}>
              <Zap size={48} strokeWidth={1.5} style={{ color: 'var(--text-faint)', marginBottom: 12 }} />
              <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
                {t('actions.journey_empty')}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {participations.map((p) => (
                <Link
                  key={p.postId}
                  to={`/posts/${p.postId}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '14px 16px',
                    borderBottom: '1px solid var(--border)',
                    textDecoration: 'none',
                  }}
                >
                  <Zap size={20} color="var(--accent)" strokeWidth={1.8} style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
                      {p.actionTitle}
                    </p>
                    <p style={{ margin: '2px 0 0', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
                      {p.group.name} · {formatDateTime(p.attestedAt, { autoHideTime: true })}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  )
}
